import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import Recruiter from "../../apiResources/Recruiter";
import Employer from "../../apiResources/Employer";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import SettingLabel from "./SettingLabel";

const RecruiterTenure = (props: Props) => {
  const {
    user,
    company,
    isEditing = false,
    disabled = false,
    onEditStart,
    onEditComplete,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    
    if (values.existingSince === company.existingSince) {
      onEditComplete();
      return;
    }

    try {
      
      company.assignAttributes(values);
      const success = await company.save();
      if (success) {
        onEditComplete();
      } else {
        formik.setErrors({
          existingSince: company.errors.existingSince?.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      formik.setErrors({
        existingSince: "Uh oh, something went wrong there",
      });
    } finally {
      company.rollback();
    }
  };

  const edit = () => {
    if (!disabled) {
      onEditStart();
    }
  };

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Existing Since</SettingLabel>
          {user.isAdmin && (
            <TextLink disabled={disabled} onClick={edit}>
              Edit
            </TextLink>
          )}
        </Header>
        {company.existingSince ? <div>{company.existingSince}</div> : <div>Not specified</div>}
      </section>
    );
  }

  const initialValues: FormValues = { existingSince: company.existingSince };

  const validation = Yup.object({
    existingSince: Yup.string().required("Please enter a existing since"),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Existing Since</SettingLabel>
            <TextLink onClick={onEditComplete}>Cancel</TextLink>
          </Header>
          <TextInputField label="Existing Since" hiddenLabel name="existingSince" type="date" />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type FormValues = {
  existingSince: string;
};

type Props = {
  user: RecruiterUser | EmployerUser;
  company: Recruiter | Employer;
  isEditing?: boolean;
  editable?: boolean;
  disabled?: boolean;
  onEditStart: () => void;
  onEditComplete: () => void;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default RecruiterTenure;
